import { CircularProgress, SelectChangeEvent } from '@mui/material';
import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import {
  getResourceSearchHeader,
  defaultOption,
  resolveInternetPageSize,
} from '~/utils/constants';
import { dropdown } from './ResourceSearch.style';
import {
  getEduCode,
  sortGrade,
  sortHighSchoolGrade,
  subjectsSort,
  yearSortAndReverse,
} from './resourceSearchSortHelpers';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/store';
import { setSearchCondition } from '~/store/resource';
import { setSearchCondition as setInterSearchCondition } from '~/store/internetResource';
import { getterClientResourceFilter } from '~/store/resource/resourceGetter';
import { TSearchCondition } from '~/store/resource/resource.type';
import { useGetSemestersQuery } from '~/api/oneBoxV2';
import { resourceEduCode } from '~/api/oneBoxV3/oneBoxV3.type';
import {
  useLazyDropDownListQuery,
  useLazyInternetDropDownListQuery,
} from '~/api/oneBoxV3';
import { getterSemesters } from '~/store/notify/notifyGetter';
import { SIDEBAR, SearchCategory } from '~/types/enum';
import { LATEST_NEWS_COMPACT_RESOURCE_FILTERS } from '~/components/LatestNews/latestNewsFeatures';
import { resourceFilterEvent } from '~/tracking';

export const setLocalSotrage = (condition: { [key: string]: string }) => {
  localStorage.setItem('resourceSearchKey', JSON.stringify(condition));
};

type TResourceSearchProps = {
  /** 置於四個篩選後方，沿用同一列排版（例如最新消息之分類標籤、關鍵字） */
  extraControls?: ReactNode;
};

export const ResourceSearch = ({ extraControls }: TResourceSearchProps) => {
  useGetSemestersQuery();
  const semesters = useSelector(getterSemesters);
  const { stage, year } = semesters[0] || {};
  const schoolYear = stage === 1 ? `${year}上` : `${year}下`;

  // 得到當前 filter 內容
  const { eduCodes, years, grades, subjects, resources1 } = useSelector(
    getterClientResourceFilter,
  );
  /** 網路資源走 internet dropDownList；其餘走校內 API */
  const isInternetContext = resources1 === SIDEBAR.internet;
  /**
   * 最新消息：預設與電子書相同為四欄；僅在 LATEST_NEWS_COMPACT_RESOURCE_FILTERS 為 true 時改回兩欄。
   */
  const isLatestNewsPhase1 =
    resources1 === SIDEBAR.latestNews && LATEST_NEWS_COMPACT_RESOURCE_FILTERS;
  const dispatch = useAppDispatch();

  const [
    fetchSubjectsDropdown,
    { data: subjectData, isFetching: isFetchingSubjects },
  ] = useLazyDropDownListQuery();
  const [
    fetchGradesDropdown,
    { data: gradesData, isFetching: isFetchingGrades },
  ] = useLazyDropDownListQuery();
  const [fetchYearsDropdown, { data: yearsData, isFetching: isFetchingYears }] =
    useLazyDropDownListQuery();

  const [
    fetchInternetSubjectsDropdown,
    { data: internetSubjectsData, isFetching: isFetchingInternetSubjects },
  ] = useLazyInternetDropDownListQuery();
  const [
    fetchInternetGradesDropdown,
    { data: internetGradesData, isFetching: isFetchingInternetGrades },
  ] = useLazyInternetDropDownListQuery();
  const [
    fetchInternetYearsDropdown,
    { data: internetYearsData, isFetching: isFetchingInternetYears },
  ] = useLazyInternetDropDownListQuery();

  const dropDownlistProps = useMemo(() => {
    return isInternetContext
      ? {
          subjects:
            internetSubjectsData?.data?.uniqInternetResourceProps?.subjects,
          years: internetYearsData?.data?.uniqInternetResourceProps?.years,
          grades: internetGradesData?.data?.uniqInternetResourceProps?.grades,
        }
      : {
          subjects: subjectData?.data?.uniqResourceProps?.subjects,
          years: yearsData?.data?.uniqResourceProps?.years,
          grades: gradesData?.data?.uniqResourceProps?.grades,
        };
  }, [
    internetSubjectsData,
    internetYearsData,
    internetGradesData,
    subjectData,
    yearsData,
    gradesData,
    isInternetContext,
  ]);

  const getPageSize = useCallback(() => {
    if (resources1 === SIDEBAR.latestNews) return 5;
    let pageSize = 30;
    if (
      resources1 &&
      (resources1 as SIDEBAR) !== SIDEBAR.ebook &&
      (resources1 as SIDEBAR) !== SIDEBAR.tiku
    ) {
      pageSize = (resources1 as SIDEBAR) === SIDEBAR.internet ? 100 : 4;
    }
    return pageSize;
  }, [resources1]);

  const filters = useMemo(() => {
    const baseEdu = {
      id: 'eduCodes' as const,
      search: eduCodes,
      options: defaultOption.eduCodes,
      disabled: false,
      isFetching: false,
    };
    const gradeFilter = {
      id: 'grades' as const,
      search: grades,
      options:
        eduCodes === resourceEduCode.H
          ? sortHighSchoolGrade(subjects || '', dropDownlistProps?.grades || [])
          : sortGrade(dropDownlistProps?.grades || ['']),
      disabled: isLatestNewsPhase1 ? !eduCodes : !(eduCodes && subjects),
      isFetching: isInternetContext
        ? isFetchingInternetGrades
        : isFetchingGrades,
    };
    if (isLatestNewsPhase1) {
      return [baseEdu, gradeFilter];
    }
    return [
      baseEdu,
      {
        id: 'subjects' as const,
        search: subjects,
        options: subjectsSort(
          dropDownlistProps?.subjects || [],
          getEduCode(eduCodes || ''),
        ),
        disabled: !eduCodes,
        isFetching: isInternetContext
          ? isFetchingInternetSubjects
          : isFetchingSubjects,
      },
      {
        id: 'years' as const,
        search: years,
        options: yearSortAndReverse(dropDownlistProps?.years || []),
        disabled: !(eduCodes && subjects),
        isFetching: isInternetContext
          ? isFetchingInternetYears
          : isFetchingYears,
      },
      gradeFilter,
    ];
  }, [
    dropDownlistProps,
    isFetchingInternetSubjects,
    isFetchingSubjects,
    isFetchingInternetGrades,
    isFetchingGrades,
    isFetchingInternetYears,
    isFetchingYears,
    isInternetContext,
    eduCodes,
    subjects,
    years,
    grades,
    isLatestNewsPhase1,
  ]);

  /**
   * 學制＋科目齊備後，學年度應預設為「可選清單中最新一筆」。
   * 若 Redux／localStorage 帶入的學年度（例如 semesters 推的 schoolYear）不在 API 回傳選項內，
   * MUI Select 會顯示空白，故在選項載入完成後校正為清單第一項。
   */
  useEffect(() => {
    if (isLatestNewsPhase1) return;
    if (!eduCodes || !subjects) return;
    const fetchingYears = isInternetContext
      ? isFetchingInternetYears
      : isFetchingYears;
    if (fetchingYears) return;

    const rawYears = isInternetContext
      ? internetYearsData?.data?.uniqInternetResourceProps?.years
      : yearsData?.data?.uniqResourceProps?.years;
    const sortedYears = yearSortAndReverse(rawYears || []);
    if (!sortedYears.length) return;

    const currentYear = (years as string) || '';
    const latestYear = sortedYears[0];
    if (currentYear && sortedYears.includes(currentYear)) return;

    const next = {
      eduCodes: eduCodes as string,
      subjects: subjects,
      years: latestYear,
      grades: (grades as string) || '',
    };
    setLocalSotrage(next);
    dispatch(
      setInterSearchCondition({
        ...next,
        pageSize: resolveInternetPageSize(resources1),
        page: 0,
      }),
    );
    dispatch(
      setSearchCondition({
        ...next,
        pageSize: getPageSize(),
        page: 0,
      }),
    );
  }, [
    isLatestNewsPhase1,
    eduCodes,
    subjects,
    grades,
    years,
    isInternetContext,
    isFetchingYears,
    isFetchingInternetYears,
    yearsData,
    internetYearsData,
    dispatch,
    getPageSize,
  ]);

  /** Phase 1 最新消息：自動補科目／學年度，讓年級 API 能取數（使用者僅操作學制、年級）。 */
  useEffect(() => {
    if (!isLatestNewsPhase1) return;
    if (!eduCodes) return;
    const subs = isInternetContext
      ? internetSubjectsData?.data?.uniqInternetResourceProps?.subjects
      : subjectData?.data?.uniqResourceProps?.subjects;
    if (!subs?.length) return;
    if (subjects && years) return;
    const filledSubjects = subjects || subs[0];
    const filledYears = years || schoolYear;
    const payload = {
      subjects: filledSubjects,
      years: filledYears,
      pageSize: getPageSize(),
      page: 0,
    };
    dispatch(
      setInterSearchCondition({
        ...payload,
        pageSize: resolveInternetPageSize(resources1),
      }),
    );
    dispatch(setSearchCondition(payload));
    setLocalSotrage({
      eduCodes: eduCodes as string,
      subjects: filledSubjects,
      years: filledYears,
      grades: (grades as string) || '',
      resources1: (resources1 as string) || '',
    });
  }, [
    isLatestNewsPhase1,
    eduCodes,
    subjects,
    years,
    grades,
    resources1,
    isInternetContext,
    schoolYear,
    subjectData,
    internetSubjectsData,
    dispatch,
    getPageSize,
  ]);

  // 網路資源：已選學制／科目但未選學年度時，與其他資源一致帶入預設學年度（清單第一筆＝最新，無則用當前學期）
  useEffect(() => {
    if (resources1 !== SIDEBAR.internet || !eduCodes || !subjects) return;
    const yearMissing = !years || years === '' || years === 'all';
    if (!yearMissing) return;
    if (isFetchingInternetYears) return;
    const list = yearSortAndReverse(dropDownlistProps?.years || []);
    const defaultYear = list[0] ?? schoolYear;
    if (!defaultYear) return;

    const nextPageSize = getPageSize();
    const base = {
      eduCodes: eduCodes as string,
      years: defaultYear,
      grades: grades ?? '',
      subjects: subjects,
      resources1: resources1 as string,
    };
    setLocalSotrage(base);
    dispatch(
      setSearchCondition({
        ...base,
        pageSize: nextPageSize,
        page: 0,
      }),
    );
    dispatch(
      setInterSearchCondition({
        ...base,
        pageSize: 100,
        page: 0,
      }),
    );
  }, [
    resources1,
    eduCodes,
    subjects,
    grades,
    years,
    schoolYear,
    isFetchingInternetYears,
    dropDownlistProps?.years,
    dispatch,
    getPageSize,
  ]);

  useEffect(() => {
    // 取得 dropdown 科目資料
    if (isInternetContext) {
      fetchInternetSubjectsDropdown({
        isLaunch: true,
        categories: [SearchCategory.SCHOOL, SearchCategory.PUBLIC],
        internetResourceProps: ['subjects'],
        search: {
          eduCodes,
        },
      }).catch(e => {
        console.error('Failed to fetch internet subjects dropdown data:', e);
      });
    } else {
      fetchSubjectsDropdown({
        isLaunch: true,
        categories: [SearchCategory.SCHOOL],
        resourceProps: ['subjects'],
        search: {
          eduCodes,
        },
      }).catch(e => {
        console.error('Failed to fetch subjects dropdown data:', e);
      });
    }
  }, [isInternetContext, eduCodes]);

  useEffect(() => {
    if (!eduCodes || !subjects) return;
    // 取得 dropdown 年級資料
    if (isInternetContext) {
      fetchInternetGradesDropdown({
        isLaunch: true,
        categories: [SearchCategory.SCHOOL, SearchCategory.PUBLIC],
        internetResourceProps: ['grades'],
        search: {
          resources1: resources1 as string,
          eduCodes,
          subjects,
          years,
        },
      }).catch(e => {
        console.error('Failed to fetch internet grades dropdown data:', e);
      });
    } else {
      fetchGradesDropdown({
        isLaunch: true,
        categories: [SearchCategory.SCHOOL],
        resourceProps: ['grades'],
        search: {
          ...(resources1 !== SIDEBAR.latestNews && resources1
            ? { resources1 }
            : {}),
          eduCodes,
          subjects,
          years,
        },
      }).catch(e => {
        console.error('Failed to fetch grades dropdown data:', e);
      });
    }
  }, [isInternetContext, resources1, eduCodes, subjects, years]);

  useEffect(() => {
    if (!eduCodes || !subjects) return;
    // 取得 dropdown 學年度資料
    if (isInternetContext) {
      fetchInternetYearsDropdown({
        isLaunch: true,
        categories: [SearchCategory.SCHOOL, SearchCategory.PUBLIC],
        internetResourceProps: ['years'],
        search: {
          resources1: resources1 as string,
          eduCodes,
          subjects,
          grades,
        },
      }).catch(e => {
        console.error('Failed to fetch internet years dropdown data:', e);
      });
    } else {
      fetchYearsDropdown({
        isLaunch: true,
        categories: [SearchCategory.SCHOOL],
        resourceProps: ['years'],
        search: {
          ...(resources1 !== SIDEBAR.latestNews && resources1
            ? { resources1 }
            : {}),
          eduCodes,
          subjects,
          grades,
        },
      }).catch(e => {
        console.error('Failed to fetch years dropdown data:', e);
      });
    }
  }, [isInternetContext, resources1, eduCodes, subjects, grades]);

  const resolvePageSizeForCondition = (condition: {
    resources1?: string;
  }): number => {
    if (condition.resources1 === SIDEBAR.latestNews) return 5;
    if (condition.resources1 === SIDEBAR.ebook) return 30;
    return getPageSize();
  };

  const handleDropdownChange = (name: string, value: unknown) => {
    let newCondition: Record<string, string> = {};
    switch (name) {
      case 'eduCodes':
        // 重新選學制：清空後續篩選並切到「最新消息」
        newCondition = {
          eduCodes: value as string,
          years: years as string,
          grades: '',
          subjects: '',
          resources1: SIDEBAR.latestNews as string,
        };
        break;
      case 'subjects':
        newCondition = {
          eduCodes: eduCodes as string,
          years: years || schoolYear,
          grades: '',
          subjects: value as string,
          ...(resources1 ? { resources1: resources1 } : {}),
        };
        break;
      case 'years':
        newCondition = {
          eduCodes: eduCodes as string,
          years: value as string,
          grades: grades as string,
          subjects: subjects as string,
          ...(resources1 ? { resources1: resources1 } : {}),
        };
        break;
      case 'grades':
        // 維持目前側欄（重新選學制後應留在「最新消息」，勿自動切電子書）
        newCondition = {
          eduCodes: eduCodes as string,
          years: years as string,
          grades: value as string,
          subjects: subjects as string,
          ...(resources1 ? { resources1: resources1 } : {}),
        };
        break;
      default:
        break;
    }
    const pageSize = resolvePageSizeForCondition(newCondition);
    setLocalSotrage(newCondition);
    dispatch(
      setInterSearchCondition({
        ...newCondition,
        pageSize: resolveInternetPageSize(
          newCondition.resources1 ?? (resources1 as string),
        ),
        page: 0,
      }),
    );
    dispatch(
      setSearchCondition({
        ...newCondition,
        pageSize,
        page: 0,
      }),
    );

    resourceFilterEvent({
      edu: newCondition.eduCodes,
      subject: newCondition.subjects,
      year: newCondition.years,
      grade: newCondition.grades,
    });
  };

  return (
    <dropdown.Container>
      {filters.map(({ id, search, options, disabled, isFetching }) => {
        return (
          <dropdown.FormControl key={id}>
            <dropdown.Label disabled={disabled}>
              {getResourceSearchHeader(id as keyof TSearchCondition)}
            </dropdown.Label>
            <dropdown.Select
              value={(search as string) ?? ''}
              label={getResourceSearchHeader(id as keyof TSearchCondition)}
              name={id}
              disabled={disabled}
              onChange={(event: SelectChangeEvent<unknown>) => {
                const { name, value } = event.target;
                handleDropdownChange(name, value);
              }}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: '50vh' },
                },
              }}
            >
              {isFetching ? (
                <dropdown.Item disabled>
                  <CircularProgress sx={{ color: '00A15D' }} size={24} />
                </dropdown.Item>
              ) : (
                options?.map(option => (
                  <dropdown.Item value={option} key={option}>
                    {option}
                  </dropdown.Item>
                ))
              )}
            </dropdown.Select>
          </dropdown.FormControl>
        );
      })}
      {extraControls}
    </dropdown.Container>
  );
};
